import React, { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Tag, Check, Plus, X, Pencil } from 'lucide-react';
import { Popover, PopoverTrigger, PopoverContent } from '../../ui/popover';
import { LABEL_COLORS } from '../../../lib/constants';

const LabelPicker = ({ value = [], labels = [], onChange, onCreateLabel, onDeleteLabel, onUpdateLabel, children, disabled = false }) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [showNewForm, setShowNewForm] = useState(false);
  const [newName, setNewName] = useState('');
  const [newColor, setNewColor] = useState('#3B82F6');
  const [editingId, setEditingId] = useState(null);
  const [editingName, setEditingName] = useState('');
  const editRowRef = useRef(null);

  // When editing starts, scroll the edit row fully into view so its color
  // swatches aren't clipped at the bottom of the scrollable list (e.g. when
  // editing the last label).
  useEffect(() => {
    if (editingId && editRowRef.current) {
      editRowRef.current.scrollIntoView({ block: 'nearest' });
    }
  }, [editingId]);

  const saveName = (label) => {
    const trimmed = editingName.trim();
    if (trimmed && trimmed !== label.name) onUpdateLabel?.(label, { name: trimmed });
  };

  const toggleLabel = (labelId) => {
    const next = value.includes(labelId)
      ? value.filter(id => id !== labelId)
      : [...value, labelId];
    onChange(next);
  };

  const handleCreate = async () => {
    if (!newName.trim() || !onCreateLabel) return;
    const created = await onCreateLabel({ name: newName.trim(), color: newColor });
    if (created) {
      onChange([...value, created.id]);
      setShowNewForm(false);
      setNewName('');
      setNewColor('#3B82F6');
    }
  };

  const selectedLabels = labels.filter(l => value.includes(l.id));

  const defaultTrigger = (
    <button disabled={disabled} className={`flex items-center gap-1.5 px-2 py-1 rounded-md text-[13px] transition-colors ${disabled ? 'opacity-50 cursor-not-allowed' : 'hover:bg-accent/60'} text-muted-foreground`}>
      <Tag className="w-3.5 h-3.5" />
      {selectedLabels.length > 0 ? (
        <div className="flex items-center gap-1">
          {selectedLabels.slice(0, 3).map(l => (
            <span key={l.id} className="flex items-center gap-1">
              <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: l.color }} />
              <span className="text-foreground/80">{l.name}</span>
            </span>
          ))}
          {selectedLabels.length > 3 && <span className="text-muted-foreground">+{selectedLabels.length - 3}</span>}
        </div>
      ) : (
        <span>{t('tasks.labels')}</span>
      )}
    </button>
  );

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        {children || defaultTrigger}
      </PopoverTrigger>
      <PopoverContent className="w-56 p-2 bg-accent border-border max-md:!w-screen max-md:!max-w-none max-md:!rounded-b-none" align="start" sideOffset={4}>
        <div
          className="max-h-[240px] overflow-y-auto space-y-0.5"
          // BG_72: this picker is rendered inside the task dialog, whose scroll
          // lock (react-remove-scroll) attaches a non-passive `wheel` listener
          // on `document` and preventDefaults any wheel event whose target sits
          // outside the dialog. Because this popover is portaled to <body>,
          // wheeling over the list was cancelled and only the scrollbar worked.
          // Stopping propagation keeps the event off the document listener so
          // the browser's native list scrolling is allowed.
          onWheel={(e) => e.stopPropagation()}
        >
          {labels.map(label => (
            editingId === label.id ? (
              <div key={label.id} ref={editRowRef} className="px-1 py-1.5 space-y-1.5">
                <div className="flex items-center gap-1">
                  <input
                    value={editingName}
                    onChange={e => setEditingName(e.target.value)}
                    autoFocus
                    placeholder={t('tasks.label_name')}
                    className="flex-1 min-w-0 px-2 py-1 bg-accent/60 border border-border rounded-md text-xs text-foreground"
                    onKeyDown={e => {
                      if (e.key === 'Enter') { saveName(label); setEditingId(null); }
                      if (e.key === 'Escape') setEditingId(null);
                    }}
                    onBlur={() => saveName(label)}
                  />
                  {onDeleteLabel && (
                    <button onMouseDown={e => e.preventDefault()} onClick={() => { onDeleteLabel(label.id); setEditingId(null); }}
                      className="p-0.5 text-red-400 hover:text-red-300 flex-shrink-0" title={t('ui_task.labels.delete_label')}>
                      <X className="w-3.5 h-3.5" />
                    </button>
                  )}
                </div>
                <div className="flex gap-0.5 flex-wrap">
                  {LABEL_COLORS.map(c => (
                    <button key={c}
                      onMouseDown={e => e.preventDefault()}
                      onClick={() => { saveName(label); onUpdateLabel?.(label, { color: c }); setEditingId(null); }}
                      className="w-4 h-4 rounded-full border border-input hover:scale-110 transition-transform"
                      style={{ backgroundColor: c }}
                    />
                  ))}
                </div>
              </div>
            ) : (
              <div key={label.id} className="flex items-center group">
                <button
                  onClick={() => toggleLabel(label.id)}
                  className={`flex-1 flex items-center gap-2 px-2.5 py-1.5 rounded-md hover:bg-accent/60 text-sm ${value.includes(label.id) ? 'bg-accent/60' : ''}`}
                >
                  <span className="w-3 h-3 rounded-full flex-shrink-0" style={{ backgroundColor: label.color }} />
                  <span className="text-foreground/80 flex-1 text-left truncate">{label.name}</span>
                  {value.includes(label.id) && <Check className="w-3.5 h-3.5 text-amber-700 dark:text-amber-400 flex-shrink-0" />}
                </button>
                <button onClick={() => { setEditingId(label.id); setEditingName(label.name); }}
                  className="opacity-0 group-hover:opacity-100 p-1 text-muted-foreground hover:text-foreground/80" title={t('ui_task.labels.edit_label')}>
                  <Pencil className="w-3 h-3" />
                </button>
              </div>
            )
          ))}
        </div>

        {showNewForm ? (
          <div className="border-t border-border mt-2 pt-2 space-y-2">
            <input
              value={newName}
              onChange={e => setNewName(e.target.value)}
              placeholder={t('tasks.label_name')}
              className="w-full px-2.5 py-1.5 bg-accent/60 border border-border rounded-md text-xs text-foreground"
              autoFocus
              onKeyDown={e => { if (e.key === 'Enter') handleCreate(); if (e.key === 'Escape') setShowNewForm(false); }}
            />
            <div className="flex gap-1">
              {LABEL_COLORS.map(c => (
                <button key={c} onClick={() => setNewColor(c)}
                  className={`w-5 h-5 rounded-full transition-transform ${newColor === c ? 'ring-2 ring-white ring-offset-1 ring-offset-[#252526] scale-110' : 'hover:scale-110'}`}
                  style={{ backgroundColor: c }}
                />
              ))}
            </div>
            <div className="flex gap-1.5">
              <button onClick={handleCreate} className="flex-1 px-2 py-1 bg-amber-500 text-black rounded-md text-xs font-medium hover:bg-amber-400">{t('common.create')}</button>
              <button onClick={() => setShowNewForm(false)} className="px-2 py-1 text-muted-foreground hover:text-foreground text-xs">{t('common.cancel')}</button>
            </div>
          </div>
        ) : (
          <button onClick={() => setShowNewForm(true)}
            className="w-full flex items-center gap-2 px-2.5 py-1.5 mt-1 border-t border-border pt-2 text-muted-foreground hover:text-foreground/80 text-xs">
            <Plus className="w-3.5 h-3.5" />
            <span>{t('tasks.new_label')}</span>
          </button>
        )}
      </PopoverContent>
    </Popover>
  );
};

export default LabelPicker;
